/**
 * Experiments page — Synthetic Control / Geo Lift studies
 *
 * Fetches job configs + job runs in parallel, joins them by jobConfigId
 * so the status badge reflects the live job run state.
 * Auto-refreshes every 15 s while any job is queued or running.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import axiosInstance from "../../utils/axiosInstance";
import DownloadReportButton from "../measurement/geo-lift/components/DownloadReportButton";

// ── Types ──────────────────────────────────────────────────────────────────────

type StudyStatus = "designing" | "pending" | "running" | "completed" | "failed";

interface Study {
  id: string;
  name: string;
  status: StudyStatus;
  createdAt: string;
  markets?: string[];
  scmType?: string;
  campaignDate?: string;
  liftPct?: number;
  iRoas?: number;
  jobRunId?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function mapStatus(configStatus: string, runStatus?: string): StudyStatus {
  const run = (runStatus || "").toUpperCase();
  const cfg = (configStatus || "").toLowerCase();

  if (run === "RUNNING")                                               return "running";
  if (run === "PENDING")                                               return "pending";
  if (run === "SUCCESSFUL" || run === "SUCCESS")                       return "completed";
  if (run === "FAILED")                                                return "failed";

  if (cfg.includes("success") || cfg.includes("complete"))            return "completed";
  if (cfg === "running")                                               return "running";
  if (cfg === "pending")                                               return "pending";
  if (cfg.includes("fail"))                                            return "failed";
  if (cfg === "draft" || cfg === "")                                   return "designing";
  return "designing";
}

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ── Status badge ───────────────────────────────────────────────────────────────

const STATUS_META: Record<StudyStatus, { label: string; color: string; bg: string }> = {
  designing: { label: "Designing",  color: "#8b5cf6", bg: "rgba(139,92,246,0.1)" },
  pending:   { label: "Queued",     color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
  running:   { label: "Running",    color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  completed: { label: "Completed",  color: "#10b981", bg: "rgba(16,185,129,0.1)" },
  failed:    { label: "Failed",     color: "#ef4444", bg: "rgba(239,68,68,0.1)"  },
};

const StatusBadge: React.FC<{ status: StudyStatus }> = ({ status }) => {
  const m = STATUS_META[status];
  const pulse = status === "running" || status === "pending";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 9px", borderRadius: 20,
      background: m.bg, color: m.color,
      fontSize: 11, fontWeight: 500, fontFamily: "var(--font-sans)",
      whiteSpace: "nowrap",
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%", background: m.color,
        animation: pulse ? "pulseDot 1.5s ease-in-out infinite" : "none",
      }} />
      {m.label}
    </span>
  );
};

// ── Study card ─────────────────────────────────────────────────────────────────

const StudyCard: React.FC<{
  study: Study;
  onClick: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}> = ({ study, onClick, onEdit, onDelete }) => {
  const m = STATUS_META[study.status];
  const isClickable = study.status === "completed";
  // Editing a study mid-flight (running/pending) would race the SQS pipeline
  // that's already processing it — only offer it for studies that are done
  // one way or another.
  const isEditable = study.status === "completed" || study.status === "failed";
  const [showMarkets, setShowMarkets] = useState(false);
  return (
    <div
      onClick={isClickable ? onClick : undefined}
      title={isClickable ? undefined : "This study isn't ready to view yet"}
      style={{
        padding: "16px 20px",
        background: "var(--bg-surface)",
        border: "1px solid var(--border-subtle)",
        borderLeft: `3px solid ${m.color}`,
        borderRadius: "var(--radius)",
        cursor: isClickable ? "pointer" : "default",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        position: "relative",
      }}
      onMouseEnter={e => { if (isClickable) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Edit + Delete buttons */}
      <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 2 }}>
        {isEditable && (
          <button
            onClick={onEdit}
            title="Edit study"
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--text-tertiary)", fontSize: 13, padding: 4,
              borderRadius: 4, lineHeight: 1,
              transition: "color 150ms ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.color = "var(--blue)")}
            onMouseLeave={e => (e.currentTarget.style.color = "var(--text-tertiary)")}
          >
            ✎
          </button>
        )}
        <button
          onClick={onDelete}
          title="Delete study"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-tertiary)", fontSize: 14, padding: 4,
            borderRadius: 4, lineHeight: 1,
            transition: "color 150ms ease",
          }}
          onMouseEnter={e => (e.currentTarget.style.color = "#ef4444")}
          onMouseLeave={e => (e.currentTarget.style.color = "var(--text-tertiary)")}
        >
          ✕
        </button>
      </div>

      {/* Title row */}
      <div style={{ marginBottom: 10, paddingRight: 44 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", fontFamily: "var(--font-sans)" }}>
            {study.name}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StatusBadge status={study.status} />
          <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)" }}>
            {fmt(study.createdAt)}
          </span>
        </div>
      </div>

      {/* Meta chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {study.markets && study.markets.length > 0 && (
          <span style={{ ...chipStyle, position: "relative", display: "inline-flex", alignItems: "center", gap: 4 }}>
            {study.markets[0]}
            {study.markets.length > 1 && (
              <span
                onMouseEnter={() => setShowMarkets(true)}
                onMouseLeave={() => setShowMarkets(false)}
                onClick={e => { e.stopPropagation(); setShowMarkets(v => !v); }}
                style={{ color: "var(--text-tertiary)", cursor: "pointer", fontWeight: 600 }}
              >
                +{study.markets.length - 1}
              </span>
            )}
            {showMarkets && study.markets.length > 1 && (
              <span
                onClick={e => e.stopPropagation()}
                style={{
                  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20,
                  padding: "6px 10px", borderRadius: 8,
                  background: "var(--bg-surface)", border: "1px solid var(--border-subtle)",
                  color: "var(--text-secondary)", fontSize: 11, fontFamily: "var(--font-sans)",
                  whiteSpace: "nowrap", textTransform: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                }}
              >
                {study.markets.slice(1).join(", ")}
              </span>
            )}
          </span>
        )}
        {study.scmType && (
          <span style={chipStyle}>{study.scmType === "single" ? "Single SCM" : study.scmType === "pooled" ? "Pooled SCM" : study.scmType}</span>
        )}
        {study.campaignDate && (
          <span style={chipStyle}>Campaign: {fmt(study.campaignDate)}</span>
        )}
      </div>

      {/* Results row — only for completed */}
      {study.status === "completed" && (
        <div style={{ display: "flex", alignItems: "center", gap: 20, paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
          {study.liftPct != null ? (
            <div>
              <div style={metaLabel}>Lift</div>
              <div style={{ ...metaValue, color: study.liftPct > 0 ? "#10b981" : "#ef4444" }}>
                {study.liftPct > 0 ? "+" : ""}{Number(study.liftPct).toFixed(2)}%
              </div>
            </div>
          ) : (
            <div>
              <div style={metaLabel}>Lift</div>
              <div style={{ ...metaValue, color: "var(--text-tertiary)" }}>—</div>
            </div>
          )}
          {study.iRoas != null && (
            <div>
              <div style={metaLabel}>iROAS</div>
              <div style={metaValue}>{Number(study.iRoas).toFixed(2)}x</div>
            </div>
          )}
          <div style={{ marginLeft: "auto", display: "flex", alignSelf: "center" }} onClick={e => e.stopPropagation()}>
            <DownloadReportButton job_config_id={study.id} variant="icon" />
          </div>
        </div>
      )}

      {/* Progress bar — running / pending */}
      {(study.status === "running" || study.status === "pending") && (
        <div style={{ paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)", marginBottom: 5 }}>
            {study.status === "pending" ? "Queued — waiting to start" : "Processing geo lift model…"}
          </div>
          <div style={{ height: 3, background: "var(--bg-hover)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 2,
              width: study.status === "running" ? "60%" : "15%",
              background: m.color,
              animation: "indeterminate 2s ease-in-out infinite",
            }} />
          </div>
        </div>
      )}
    </div>
  );
};

const chipStyle: React.CSSProperties = {
  padding: "2px 8px", borderRadius: 12,
  background: "var(--bg-hover)", color: "var(--text-secondary)",
  fontSize: 11, fontFamily: "var(--font-sans)", textTransform: "capitalize",
};
const metaLabel: React.CSSProperties = {
  fontSize: 10, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2,
};
const metaValue: React.CSSProperties = {
  fontSize: 18, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-mono)",
};

// ── Filter tabs ────────────────────────────────────────────────────────────────

type FilterKey = "completed" | "active" | "failed";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "completed", label: "Completed" },
  { key: "active",    label: "Active"    },
  { key: "failed",    label: "Failed"    },
];

function matchFilter(s: Study, f: FilterKey): boolean {
  if (f === "active")    return s.status === "running" || s.status === "pending" || s.status === "designing";
  if (f === "completed") return s.status === "completed";
  if (f === "failed")    return s.status === "failed";
  return true;
}

// ── Delete confirm modal ───────────────────────────────────────────────────────

const DeleteModal: React.FC<{
  study: Study;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ study, onConfirm, onCancel, loading }) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center",
  }}
    onClick={onCancel}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: "var(--bg-surface)", borderRadius: "var(--radius)",
        padding: "24px 28px", maxWidth: 380, width: "90%",
        border: "1px solid var(--border-default)",
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8, fontFamily: "var(--font-sans)" }}>
        Delete "{study.name}"?
      </div>
      <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 20, fontFamily: "var(--font-sans)", lineHeight: 1.5 }}>
        This will permanently delete the study, all associated job runs, and S3 data. This cannot be undone.
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} disabled={loading} style={cancelBtnStyle}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={deleteBtnStyle}>
          {loading ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

const cancelBtnStyle: React.CSSProperties = {
  padding: "7px 16px", borderRadius: "var(--radius)",
  background: "var(--bg-hover)", color: "var(--text-secondary)",
  border: "1px solid var(--border-default)", fontSize: 13,
  cursor: "pointer", fontFamily: "var(--font-sans)",
};
const deleteBtnStyle: React.CSSProperties = {
  padding: "7px 16px", borderRadius: "var(--radius)",
  background: "#ef4444", color: "#fff",
  border: "none", fontSize: 13,
  cursor: "pointer", fontFamily: "var(--font-sans)", fontWeight: 500,
};

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ExperimentsPage() {
  const navigate = useNavigate();
  const [studies, setStudies]       = useState<Study[]>([]);
  const [filter, setFilter]         = useState<FilterKey>("completed");
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [apiError, setApiError]     = useState<string | null>(null);
  const [toDelete, setToDelete]     = useState<Study | null>(null);
  const [deleting, setDeleting]     = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setApiError(null);
    try {
      const [cfgRes, runRes] = await Promise.allSettled([
        axiosInstance.get("/api/jobConfig/jobType/GEO_LIFT?limit=200"),
        axiosInstance.get("/api/jobRun/jobType/GEO_LIFT?limit=200"),
      ]);

      if (cfgRes.status === "rejected") {
        const msg = (cfgRes.reason as any)?.response?.data?.detail || (cfgRes.reason as any)?.message || "Failed to load job configs";
        setApiError(`Job configs API error: ${msg}`);
        return;
      }
      if (runRes.status === "rejected") {
        const msg = (runRes.reason as any)?.response?.data?.detail || (runRes.reason as any)?.message || "Failed to load job runs";
        setApiError(`Job runs API error: ${msg}`);
      }

      const configs: any[] = Array.isArray(cfgRes.value.data)
        ? cfgRes.value.data
        : cfgRes.value.data?.items ?? [];
      const runs: any[] = runRes.status === "fulfilled"
        ? (Array.isArray(runRes.value.data) ? runRes.value.data : runRes.value.data?.items ?? [])
        : [];

      // Build map: jobConfigId → latest run
      const latestRun: Record<string, any> = {};
      for (const r of runs) {
        const cid = r.jobConfigId;
        if (!cid) continue;
        if (!latestRun[cid] || new Date(r.createdDate || r.jobExecutionTime || 0) > new Date(latestRun[cid].createdDate || latestRun[cid].jobExecutionTime || 0)) {
          latestRun[cid] = r;
        }
      }

      const configIds = new Set(configs.map((jc: any) => jc.jobConfigId || jc.id));

      // GeoLift jobs created by the Command Center (demo) belong to its tests, not to Experiments.
      const studyConfigs = configs.filter((jc: any) => jc.modelParameterConfig?.parameters?.source !== "command_center");

      const fromConfigs: Study[] = studyConfigs.map((jc: any) => {
        const id     = jc.jobConfigId || jc.id;
        const run    = latestRun[id];
        const params = jc.modelParameterConfig?.parameters || {};
        const output = jc.modelOutputConfig?.outputData || jc.modelOutputConfig || {};
        const lift   = jc.aggregateLift != null ? parseFloat(jc.aggregateLift) : (output.aggregateLift != null ? parseFloat(output.aggregateLift) : undefined);
        const roas   = output.roas           != null ? parseFloat(output.roas)          : undefined;

        const markets: string[] | undefined = Array.isArray(params.includeMarket) && params.includeMarket.length > 0
          ? params.includeMarket
          : params.selectedMarket
            ? [params.selectedMarket]
            : undefined;

        return {
          id,
          name:         jc.name || "Geo Lift Study",
          status:       mapStatus(jc.status || "", run?.jobStatus),
          createdAt:    jc.createdDate || jc.createdAt || new Date().toISOString(),
          markets,
          scmType:      params.scmType || undefined,
          campaignDate: params.campaignStartDate || undefined,
          liftPct:      isNaN(lift as number) ? undefined : lift,
          iRoas:        isNaN(roas as number) ? undefined : roas,
          jobRunId:     run?.jobRunId,
        };
      });

      // Include orphan runs — job_run rows whose job_config_id has no matching config.
      // This happens when _create_job_config fails silently but _run_geo_lift_pipeline
      // still persists the job_run. Without this, the card never appears.
      const orphanStudies: Study[] = [];
      for (const r of runs) {
        const cid = r.jobConfigId;
        if (!cid || configIds.has(cid)) continue;
        // Only show the latest run per orphan config id
        if (latestRun[cid] && latestRun[cid].jobRunId !== r.jobRunId) continue;
        orphanStudies.push({
          id:          cid,
          name:        "Geo Lift Study",
          status:      mapStatus("", r.jobStatus),
          createdAt:   r.createdDate || r.jobExecutionTime || new Date().toISOString(),
          jobRunId:    r.jobRunId,
        });
      }

      const mapped: Study[] = [...fromConfigs, ...orphanStudies];

      // Latest first. Each filter tab (Active/Completed/Failed) already
      // separates by status via matchFilter() below — sorting by status
      // priority *before* date here would group e.g. all "pending" studies
      // before all "running" ones inside the merged Active tab, hiding a
      // newer running study behind an older queued one.
      mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setStudies(mapped);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      setApiError(`Failed to load experiments: ${msg}`);
      console.error("Failed to load experiments", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any job is active
  useEffect(() => {
    const hasActive = studies.some(s => s.status === "running" || s.status === "pending");
    if (hasActive) {
      timerRef.current = setInterval(() => load(true), 15_000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [studies, load]);

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/api/jobConfig/${toDelete.id}`);
      setStudies(prev => prev.filter(s => s.id !== toDelete.id));
      setToDelete(null);
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      setDeleting(false);
    }
  };

  const displayed = studies.filter(s => matchFilter(s, filter));
  const counts = {
    active:    studies.filter(s => matchFilter(s, "active")).length,
    completed: studies.filter(s => s.status === "completed").length,
    failed:    studies.filter(s => s.status === "failed").length,
  };

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1100, margin: "0 auto", fontFamily: "var(--font-sans)" }}>

      {/* ── CSS for animations ── */}
      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.7); }
        }
        @keyframes indeterminate {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>Experiments</h1>
            {refreshing && (
              <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-sans)" }}>Refreshing…</span>
            )}
          </div>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "4px 0 0" }}>
            Geo Lift studies
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => load(true)}
            title="Refresh"
            style={{
              padding: "9px 12px", borderRadius: "var(--radius)",
              background: "var(--bg-surface)", color: "var(--text-secondary)",
              border: "1px solid var(--border-default)", fontSize: 14,
              cursor: "pointer", lineHeight: 1,
            }}
          >
            ↻
          </button>
          <button
            onClick={() => { const id = uuidv4(); navigate(`/experiments/geo-lift/${id}?mode=evaluate&manual=true`); }}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "9px 16px", borderRadius: "var(--radius)",
              background: "var(--bg-surface)", color: "var(--text-secondary)",
              fontSize: 13, fontWeight: 500, cursor: "pointer",
              border: "1px solid var(--border-default)", fontFamily: "var(--font-sans)",
              transition: "opacity 150ms ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.75")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>⚙</span>
              New Experiment
          </button>
          <button
            onClick={() => navigate("/assistant")}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "9px 16px", borderRadius: "var(--radius)",
              background: "var(--blue)", color: "#fff",
              fontSize: 13, fontWeight: 500, cursor: "pointer",
              border: "none", fontFamily: "var(--font-sans)",
              transition: "opacity 150ms ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>✦</span>
              AI Mode
          </button>
        </div>
      </div>

      {/* ── API error banner ── */}
      {apiError && (
        <div style={{
          marginBottom: 16, padding: "10px 14px", borderRadius: "var(--radius)",
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
          color: "#ef4444", fontSize: 12, fontFamily: "var(--font-sans)",
          display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <span style={{ flexShrink: 0, fontWeight: 600 }}>Error</span>
          <span style={{ wordBreak: "break-word" }}>{apiError}</span>
          <button
            onClick={() => setApiError(null)}
            style={{ marginLeft: "auto", background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14, flexShrink: 0 }}
          >✕</button>
        </div>
      )}

      {/* ── Filter tabs ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {FILTERS.map(({ key, label }) => {
          const active = filter === key;
          const count  = counts[key];
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: "5px 12px", borderRadius: 20, fontSize: 12,
                fontFamily: "var(--font-sans)", fontWeight: active ? 500 : 400,
                background: active ? "var(--blue-subtle)" : "var(--bg-surface)",
                color:      active ? "var(--blue)"        : "var(--text-secondary)",
                border:     `1px solid ${active ? "var(--blue)" : "var(--border-default)"}`,
                cursor: "pointer", transition: "all 150ms ease",
              }}
            >
              {label}{" "}
              <span style={{ fontFamily: "var(--font-mono)", color: active ? "var(--blue)" : "var(--text-tertiary)" }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Grid ── */}
      {loading ? (
        <SkeletonGrid />
      ) : displayed.length === 0 ? (
        <EmptyState filter={filter} hasAny={studies.length > 0} onCreate={() => navigate("/assistant")} onManual={() => { const id = uuidv4(); navigate(`/experiments/geo-lift/${id}?mode=evaluate&manual=true`); }} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {displayed.map(study => (
            <StudyCard
              key={study.id}
              study={study}
              onClick={() => navigate(`/experiments/geo-lift/${study.id}?name=${encodeURIComponent(study.name)}`)}
              onEdit={e => { e.stopPropagation(); navigate(`/experiments/geo-lift/${study.id}?name=${encodeURIComponent(study.name)}&edit=true&mode=evaluate`); }}
              onDelete={e => { e.stopPropagation(); setToDelete(study); }}
            />
          ))}
        </div>
      )}

      {/* ── Delete confirm ── */}
      {toDelete && (
        <DeleteModal
          study={toDelete}
          onConfirm={handleDelete}
          onCancel={() => setToDelete(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}

// ── Skeleton loader ────────────────────────────────────────────────────────────

const SkeletonGrid = () => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
    {[1, 2, 3, 4].map(i => (
      <div key={i} style={{
        height: 150, borderRadius: "var(--radius)",
        background: "linear-gradient(90deg, var(--bg-surface) 25%, var(--bg-hover) 50%, var(--bg-surface) 75%)",
        backgroundSize: "800px 100%",
        animation: "shimmer 1.5s ease-in-out infinite",
        border: "1px solid var(--border-subtle)",
      }} />
    ))}
  </div>
);

// ── Empty state ────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ filter: FilterKey; hasAny: boolean; onCreate: () => void; onManual: () => void }> = ({ filter, hasAny, onCreate, onManual }) => (
  <div style={{ textAlign: "center", padding: "80px 24px" }}>
    <div style={{ fontSize: 44, marginBottom: 16, opacity: 0.6 }}>🧪</div>
    <p style={{ fontSize: 15, fontWeight: 500, color: "var(--text-secondary)", marginBottom: 6, fontFamily: "var(--font-sans)" }}>
      {hasAny ? `No ${filter} experiments` : "No experiments yet"}
    </p>
    <p style={{ fontSize: 13, color: "var(--text-tertiary)", marginBottom: 24, fontFamily: "var(--font-sans)" }}>
      {hasAny ? "Change the filter or create a new experiment" : "Create your first Synthetic Control study"}
    </p>
    {!hasAny && (
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button
          onClick={onManual}
          style={{
            padding: "9px 20px", borderRadius: "var(--radius)",
            background: "var(--bg-surface)", color: "var(--text-secondary)",
            fontSize: 13, cursor: "pointer", border: "1px solid var(--border-default)",
            fontFamily: "var(--font-sans)", fontWeight: 500,
          }}
        >
          ⚙ New Experiment
        </button>
        <button
          onClick={onCreate}
          style={{
            padding: "9px 20px", borderRadius: "var(--radius)",
            background: "var(--blue)", color: "#fff",
            fontSize: 13, cursor: "pointer", border: "none",
            fontFamily: "var(--font-sans)", fontWeight: 500,
          }}
        >
          ✦ AI Mode
        </button>
      </div>
    )}
  </div>
);
